precision highp float;

uniform float uTime;
uniform vec2 uResolution;
uniform float uBass;
uniform float uMid;
uniform float uHigh;
uniform float uAudioAverage;
uniform float uZoom;
uniform vec2 uOffset;
uniform vec3 uColorPrimary;
uniform vec3 uColorSecondary;
uniform vec3 uColorAccent;
uniform vec2 uDeviceTilt;

varying vec2 vUv;

const int MAX_ITERATIONS = 150;
const float PI = 3.14159265359;

// Golden ratio
const float PHI = 1.618033988749;

mat2 rotate2D(float a) {
  float c = cos(a);
  float s = sin(a);
  return mat2(c, -s, s, c);
}

void main() {
  vec2 uv = (gl_FragCoord.xy - 0.5 * uResolution.xy) / min(uResolution.x, uResolution.y);
  
  // Navigation along spiral - offset moves along the arm
  // Device tilt adds subtle navigation feel
  vec2 offset = uOffset + uDeviceTilt * 0.4;
  uv = uv * 2.0 / uZoom + offset;
  
  // Convert to polar for spiral navigation feel
  float r = length(uv);
  float theta = atan(uv.y, uv.x);
  
  // Logarithmic spiral transformation
  float spiralAngle = uTime * 0.2;
  theta += spiralAngle;
  
  // Add golden ratio based spiral arms
  float armPhase = theta + log(r + 0.1) * PHI;
  float armPattern = sin(armPhase * 5.0) * 0.5 + 0.5;
  
  // Julia set in spiral coordinates
  vec2 z = vec2(r * cos(theta), r * sin(theta));
  vec2 c = vec2(
    -0.7 + sin(uTime * 0.1) * 0.05,
    0.27 + cos(uTime * 0.12) * 0.05
  );
  
  float iterations = 0.0;
  float minDist = 1e10;
  
  for (int i = 0; i < MAX_ITERATIONS; i++) {
    if (dot(z, z) > 4.0) break;
    
    float xTemp = z.x * z.x - z.y * z.y + c.x;
    z.y = 2.0 * z.x * z.y + c.y;
    z.x = xTemp;
    
    // Track orbit for path visualization
    minDist = min(minDist, length(z));
    
    iterations += 1.0;
  }
  
  float t = iterations / float(MAX_ITERATIONS);
  float orbitTrap = 1.0 - exp(-minDist * 3.0);
  
  vec3 col;
  
  // Create navigation path visualization
  float pathGlow = smoothstep(0.3, 0.0, abs(armPattern - 0.5));
  pathGlow *= 1.0 - smoothstep(0.0, 2.0, r);
  
  if (t >= 1.0) {
    // Inside set - navigation waypoints
    col = uColorPrimary * (0.4 + orbitTrap * 0.3);
    col += uColorAccent * pathGlow * 0.5;
  } else {
    // Outside - the navigable space
    float phase = t * 4.0 + armPhase + uTime * 0.1;
    
    col = uColorSecondary * (0.4 + 0.4 * sin(phase));
    col += uColorAccent * (0.3 + 0.3 * cos(phase + 1.0));
    
    // Highlight the spiral path
    col += uColorAccent * pathGlow * 0.6;
    
    // Distance-based fading
    col *= 0.7 + 0.3 * (1.0 - t);
  }
  
  // Audio makes the path pulse (stronger)
  col *= 1.0 + uMid * pathGlow * 0.8;
  col += uColorAccent * uBass * 0.25 * (1.0 - t);
  
  // Compass-like radial indicators
  float compassLines = abs(sin(theta * 8.0)) * 0.1;
  compassLines *= smoothstep(1.5, 0.5, r);
  col += uColorAccent * compassLines * 0.2;
  
  // Center point glow - the "you are here"
  float centerGlow = exp(-r * 3.0);
  col += uColorAccent * centerGlow * (0.5 + uBass * 0.3);
  
  // Subtle motion blur in spiral direction
  float motionBlur = 0.95 + 0.05 * sin(armPhase * 20.0 + uTime * 5.0);
  col *= motionBlur;
  
  // Vignette
  vec2 vigUv = vUv * 2.0 - 1.0;
  float vignette = 1.0 - dot(vigUv, vigUv) * 0.3;
  col *= vignette;
  
  gl_FragColor = vec4(col, 1.0);
}

